import { type Clip, type ClipAssets, ClipType } from '@air/api/types';
import { isPdfRenderedExtension } from '@air/utils-clips';

/**
 * Structural minimum this mapping needs from a clip, so tests can exercise it with a plain object
 * instead of a full `Clip` fixture.
 */
export type WorkspaceAgentPillClipSource = Pick<
  Clip,
  'assetVersionCount' | 'duration' | 'ext' | 'id' | 'importedName' | 'size' | 'title' | 'type'
> &
  /** `Clip` types dimensions as numbers, but audio rows come back without them. */
  Partial<Pick<Clip, 'height' | 'width'>> & {
    assets: Pick<ClipAssets, 'image' | 'original'>;
  };

export type WorkspaceAgentPillClip = {
  duration: number | null;
  ext: string;
  height: number | null;
  name: string;
  size: number;
  thumbnailUrl: string | null;
  type: ClipType;
  /** `null` means the response omitted it (a clip with no version number), not zero. */
  versionCount: number | null;
  width: number | null;
};

const IMAGE_CLIP_TYPES = new Set([ClipType.animated, ClipType.livePhoto, ClipType.photo]);

const toWorkspaceAgentPillClipThumbnailUrl = (clip: WorkspaceAgentPillClipSource): string | null => {
  const imageUrl = clip.assets.image || null;

  if (imageUrl) {
    return imageUrl;
  }

  if (clip.assets.original && IMAGE_CLIP_TYPES.has(clip.type) && !isPdfRenderedExtension(clip.ext)) {
    return clip.assets.original;
  }

  return null;
};

/** Keyed by clip id, which is the version id a pill carries as `versionId`. */
export const toWorkspaceAgentPillClipsById = (
  clips: WorkspaceAgentPillClipSource[],
): Map<string, WorkspaceAgentPillClip> =>
  new Map(
    clips.map((clip) => [
      clip.id,
      {
        duration: clip.duration ?? null,
        ext: clip.ext,
        height: clip.height ?? null,
        name: clip.title || clip.importedName || 'Untitled',
        size: clip.size,
        thumbnailUrl: toWorkspaceAgentPillClipThumbnailUrl(clip),
        type: clip.type,
        versionCount: clip.assetVersionCount ?? null,
        width: clip.width ?? null,
      },
    ]),
  );
